module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h2', {
    staticClass: "cn"
  }, [_c('a', {
    attrs: {
      "id": "page_7"
    }
  }), _vm._v("Chapter 2")]), _vm._v(" "), _c('h2', {
    staticClass: "ct"
  }, [_vm._v("Warfare and Imperialism in Early Carthage")]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_c('span', {
    staticClass: "dcap"
  }, [_vm._v("T")]), _vm._v("he early history of Carthaginian warfare is very hard to present as a coherent narrative. Unlike in the later chapters, this one is roughly divided into thematic sections, rather than proceeding in a chronological fashion. Each section examines a particular strand of history. It first looks at the foundation myth of Carthage and discusses the possible early conflicts with indigenous groups in North Africa, as well as the spread of Punic power from Libya to the Atlantic coast. The second examines the evidence for Carthaginian interest in islands, ranging from the Balearics to those of the central Mediterranean. The third traces the stories of some of the earliest Punic leaders we know by name, such as Malchus and Mago and his descendants. The fourth discusses the series of conflicts which pitted Carthage against various Phocaean Greek colonies in the north Tyrrhenian Sea. The fifth looks at the stories of a Greek adventurer/colonist, Dorieus of Sparta, and their conflicts with the Phoenicians, Elymians, Macae, and possibly Carthaginians. In the concluding section of this chapter, all of this evidence will be examined to try to piece together an idea of what kind of wars Carthage may have been fighting before the start of the ongoing conflicts with the Sicilian Greeks and address the question of early Punic imperialism.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("As can be gleaned from the first chapter of this book, the events discussed in this chapter are only known from much later historical sources. Some, such as the Battle of Alalia, were recorded in earlier historians, such as Herodotus, but even then, this occurred over 100 years before he wrote his "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Histories")]), _vm._v(". This causes a problem in interpretation and makes us question whether what we read is historically reliable. It is impossible to definitively answer the question of ‘is this true?’ when confronted with wayward historical ‘facts’ like those upon which we must build any history of Carthage’s early ambitions and wars. As Richard Miles put so eruditely, ‘researching a history of the city is rather like reading a transcript of a conversation in which one participant’s contribution has "), _c('a', {
    attrs: {
      "id": "page_8"
    }
  }), _vm._v("been deleted.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_1",
      "id": "B_ch2_1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1")])]), _vm._v(" He was speaking specifically about the almost complete loss of Punic literature, but it is an apt metaphor for the sources with which we are left. Even within the Greek and Roman "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("corpi")]), _vm._v(", we are confronted by major historical lacunae. Thus, although the evidence used in this section is treated critically, it is important to keep in mind that for many Greeks and Romans, the Carthaginians, and Phoenicians more broadly, were useful antagonists and that much of what we read of them may be exaggerations based on biases held by ancient authors.")]), _vm._v(" "), _c('p', {
    staticClass: "head1"
  }, [_c('span', {
    staticClass: "bold"
  }, [_vm._v("The Foundation of Carthage and Conflicts in Africa")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Carthage was founded by settlers who canonically originated in the Levantine city of Tyre. It was one of many cities to which we refer collectively as ‘Phoenician’. However, this is a loaded term, as are so many ethnic nomenclatures, as those it describes would never have called themselves this. It is questionable if all of the different groups of ‘Phoenicians’ would have identified as a single ethnicity – or race – in the ancient period. The case has been strongly made recently that the communal identities of these peoples was complex."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_2",
      "id": "B_ch2_2",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2")])]), _vm._v(" Their identities may have been focused more on individual cities or city-states than in any wider way. It is clear, however, that these peoples who originated on the coast of what is now Syria, Lebanon, and Israel began to emigrate throughout the Mediterranean basin in the early Iron Age."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_3",
      "id": "B_ch2_3",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Emigrants from this region, almost certainly from Tyre, began to settle on a small peninsula on the North African coast by the middle of the eighth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(". Here they would go on to found the city of Carthage, or "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Qart-Hadasht")]), _vm._v(". The meaning of the city’s name in its own language was ‘New City’, an indication that it was a purposeful foundation and not an accidental conglomeration of traders and adventurers. Foundation stories abound regarding how it came to be, but how much truth there is in any of them remains to be see. Whether or not a late ninth-century princess named Elissa fled her evil brother Pygmalion and founded the city can never be known, while the shameful love affair between an alternative mother-queen, Dido, and the Romano-Trojan hero Aeneas is almost certainly a later fabrication. What we can say about the foundation of the city, though, is that it happened, and that the eighth-century immigrants "), _c('a', {
    attrs: {
      "id": "page_9"
    }
  }), _vm._v("to this corner of ancient Libya set in motion the development of one of the greatest cities of antiquity."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_4",
      "id": "B_ch2_4",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The early relations with native peoples may have been relatively peaceful, and the stories about the city’s foundation make it sound as though they were happy to have the new settlers as long as they paid them tribute. A yearly dispensation was given to the local tribe."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_5",
      "id": "B_ch2_5",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("5")])]), _vm._v(" If this is historical, the fact that it wasn’t thrown off until the fifth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(" may mean that Carthage did not look to expand at the expense of its most immediate neighbours for the first three hundred years of its existence. But successful campaigns against unspecified Africans were carried out by the earliest known "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("rab")]), _vm._v(", or general, in the city’s history: Mazeus (discussed in more detail below)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_6",
      "id": "B_ch2_6",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("6")])]), _vm._v(" These early wars may have been directed at native African peoples who were otherwise hostile to Carthage, i.e. not those to whom the city was paying tribute. But the ruling clan which followed Mazeus, known as the Magonids, waged much wider campaigns. Although the founder of this dynasty, Mago, is said to have greatly increased the city’s territorial possessions, we do not have details of his campaign(s). His sons, Hasdrubal and Hamilcar (who was killed at Himera in 480 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v("), would fight against the Africans to whom the city paid tribute, but were unsuccessful and the conflict only ended after a sum was paid by Carthage. However, the sons of this generation, six in number (though one was exiled), would establish the Carthaginians as the pre-eminent power in this part of Africa. After another war against the Libyans that collected Punic tributes, this time ending in a victory, the payments were brought to an end. They also campaigned against the Numidians, whose lands existed on the fringes of Carthaginian and Libyan territories. Mago’s grandsons were also supposed to have fought against the Mauri, a tribe who lived in what is today Morocco. The latter conflict could have been in defence of trading interests in the west."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_7",
      "id": "B_ch2_7",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("7")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Where exactly these conquests are to be located is a hard question to answer. There were certainly areas of Libya that Carthage controlled by the sixth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(", including the Syrtis Major – perhaps stemming from the conflict against Dorieus (see below) – and this may be land claimed by them in the first treaty with Rome in c. 509 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(". But there is archaeological evidence that the Carthaginians were investing heavily in protecting the area around Cape Bon and the Syrtis Minor in the "), _c('a', {
    attrs: {
      "id": "page_10"
    }
  }), _vm._v("fifth century, and this may be related to the Magonid campaigning we hear about in Justin. Kerkouane, a Punic colony, already had been settled on the cape in about 550 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v("."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_8",
      "id": "B_ch2_8",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("8")])]), _vm._v(" But this was followed up in the fifth century by a defensive position at Kelibia on the south coast of the peninsula."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_9",
      "id": "B_ch2_9",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("9")])]), _vm._v(" This would have helped guard against ships sailing around Cape Bon. Numerous other colonies or fortresses dot the landscape."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_10",
      "id": "B_ch2_10",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("10")])]), _vm._v(" These settlements, large and small, were probably meant to secure the hinterland for Carthage. Ancient states throughout the Mediterranean practised this type of policy by sending out defensive settlements, with Rome being one of the most prominent."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_11",
      "id": "B_ch2_11",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("11")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("How exactly Carthage maintained their possessions in Africa is unknown. As we do not have texts that describe their policies, we can only assume that it was through the usual combination in the ancient world: alliances and force. Of the former we know more in later periods. During the second war with Rome, for instance, the Carthaginians were allied to a Numidian king named Syphax. Although he would eventually lose his kingdom, while he was a friend of Carthage he ensured that his people did not harass those of the city. Likewise, the Libyan tribe to whom the Carthaginians at first paid tribute may be viewed in a similar light. A system of alliances and dependent states was an important aspect of how Rome maintained control of Italy."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_12",
      "id": "B_ch2_12",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("12")])]), _vm._v(" Maintaining control through force of arms, for instance by installing garrisons or taking hostages, was also a widely used tactic in the Mediterranean. Carthage was certainly doing this on Sicily throughout its period of dominating much of the island, and there is no reason to think that Punic garrisons were not scattered throughout Libya. But, however they maintained their hold, it was always tenuous. We find rebellions by native peoples, generally just referred to as Libyans, all through Carthaginian history. Numidians, as well, would revolt and cause chaos in North Africa. In this way, Carthage was less successful than Rome, who seemed able to maintain control in areas for longer periods between rebellions. Nevertheless, it appears that early in the city’s history its hinterland in North Africa was conquered to some degree. While there would be campaigning throughout the next few centuries, we know relatively little about it compared to Carthage’s wars with the Greeks and the Romans.")]), _vm._v(" "), _c('p', {
    staticClass: "head1"
  }, [_c('a', {
    attrs: {
      "id": "page_11"
    }
  }), _c('span', {
    staticClass: "bold"
  }, [_vm._v("Carthage, Sicily, and Two Failed Colonies: Pentathlus and Dorieus")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Dominating the horizon in northwest Sicily, near Trapani (ancient Drepana), is a mountain peak known today as Mount Erice, but to the ancients as Mount Eryx."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_13",
      "id": "B_ch2_13",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("13")])]), _vm._v(" On its peak was a celebrated temple dedicated to Astarte, Venus, and on its slope was a town which bore the mountain’s name. The original settlement was ethnically Elymian. The Elymians were an indigenous population of western Sicily in the period before Phoenician and Greek colonization. Little is known of their civilization and language, with its place in linguistic history even in dispute. From at least the sixth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(", they interacted in a friendly manner with the Phoenicians who resided on this coast of Sicily, but had a mixed relationship with the Hellenes of the island. Eventually, though, Elymian culture became rather Hellenized. This is despite constantly coming into conflict with the Greek city of Selinus with whom Segesta, one of the principal Elymian cities, shared a border.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Sometime around 580 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(", an expedition set out from Cnidus and Rhodes, the former a Greek city in Asia Minor, to found a settlement in the west."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_14",
      "id": "B_ch2_14",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("14")])]), _vm._v(" This was, perhaps, driven by the poor treatment of the Cnidians at the hands of unnamed ‘kings of Asia’, as Diodorus relates."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_15",
      "id": "B_ch2_15",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("15")])]), _vm._v(" The voyage was led by a man named Pentathlus, noted as being from Cnidus. They sailed from the eastern Mediterranean to Sicily, where they intended to settle. Rather than landing and attempting to found a colony on the ever more crowded eastern side of the island, they went to the area around which would become the Phoenician settlement of Lilybaeum."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_16",
      "id": "B_ch2_16",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("16")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The proposed placement of this colony was not haphazard. It was a strategic point which separated the Phoenician settlement of Motya from the cities of their kinsmen to the north, as well as from the Elymians, with whom the Phoenicians maintained good relations, and probably something like an alliance."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_17",
      "id": "B_ch2_17",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("17")])]), _vm._v(" As A. J. Graham put it, ‘this colony plainly threatened Motya’s existence as a port and the symbiotic relationship of Phoenicians and Elymians in western Sicily.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_18",
      "id": "B_ch2_18",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("18")])]), _vm._v(" This location was also along the sea-route between North Africa, Sardinia-Corsica, and Italy, which was heavily trafficked by Carthaginians, Phoenicians, Greeks, and those with whom they traded."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_19",
      "id": "B_ch2_19",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("19")])]), _vm._v(" Pentathlus and his companions may have been directed to this spot by some of the Greeks already inhabiting Sicily, perhaps those of Selinus, who often found themselves in conflict with "), _c('a', {
    attrs: {
      "id": "page_12"
    }
  }), _vm._v("the Elymians. Cutting off Motya would have been in their interest."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_20",
      "id": "B_ch2_20",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("20")])]), _vm._v(" Even if this was the case, we should not conclude, as some commentators have, that this was the beginning of a Greek bloc which aimed to ‘drive them [the Phoenicians] out from the island’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_21",
      "id": "B_ch2_21",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("21")])]), _vm._v(" Certainly, trade and other peaceful interactions occurred between Phoenician and Hellene on Sicily and abroad.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Regardless of whether or not they were sent to Lilybaeum by Sicilian Greeks, Pentathlus’ group eventually found itself in the middle of a war between Selinus and an alliance of Phoenicians and the Elymian city of Segesta. Of the two version of the story we possess, that by Diodorus and the other by Pausanias, there is discord on this conflict. According to the former, when the would-be colonists arrived on Sicily, they found themselves in the middle of an ongoing war, and he only mentions the Selinuntes and Segestans."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_22",
      "id": "B_ch2_22",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("22")])]), _vm._v(" The latter, however, relates that once Pentathlus had founded his new settlement, they ‘were hard pressed in a war with the Elymians and Phoenicians, and driven out.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_23",
      "id": "B_ch2_23",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("23")])]), _vm._v(" I am inclined to believe that they had founded their new settlement, though it was probably not very developed by the time they were expelled. The war between Selinus and Segesta had probably been raging, on and off, for a number of years before Pentathlus’ arrival, with the former having expanded its territory to Poggioreale."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_24",
      "id": "B_ch2_24",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("24")])]), _vm._v(" The Cnidian and Rhodian interlopers may have been the catalyst for bringing the Phoenicians into the conflict. Pentathlus was killed in the war, and his companions expelled from the island. They were not discouraged, though, and founded a successful community on Lipara."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_25",
      "id": "B_ch2_25",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("25")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Although the Carthaginians are not mentioned in our sources as having participated in this conflict, it may have been enough to draw their attention."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_26",
      "id": "B_ch2_26",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("26")])]), _vm._v(" While they did not send troops to Sicily, discussions may have begun between Punic officials and representatives of the Phoenician and Elymian cities about some sort of alliance. Lilybaeum’s proximity to Motya, the primary Phoenician settlement on the island at the time, would have been enough to set this in motion. Pentathlus’ expedition’s proximity to Motya, and the war more generally, probably spurred on the construction of that city’s first fortification wall, which is dated to the second quarter of the sixth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v("."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_27",
      "id": "B_ch2_27",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("27")])]), _vm._v(" This would have been a major project, drawing further attention to the potential for violence in western Sicily.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_13"
    }
  }), _vm._v("These rumblings may have deepened Carthaginian interest in the Phoenician colonies of Sicily, and, probably at their request, Punic leaders eventually took action. The earliest known military commander to go to Sicily is known to us through Justin as Mazeus."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_28",
      "id": "B_ch2_28",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("28")])]), _vm._v(" He seems to have begun his career campaigning in Africa, presumably against indigenous peoples. But he then went on to wage war on Sicily. Both of our sources for his life consider this to have been a failure, saying that the Carthaginians had ‘long fought unsuccessfully in Sicily’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_29",
      "id": "B_ch2_29",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("29")])]), _vm._v(" This, however, seems to be contradicted one line later in Justin’s account, as he claims that under Mazeus the Carthaginians ‘conquered a part of Sicily’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_30",
      "id": "B_ch2_30",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("30")])]), _vm._v(" Additionally, his son Carthalo had been sent to Tyre with some of the spoils from Sicily to make a tithe to Melqart."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_31",
      "id": "B_ch2_31",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("31")])]), _vm._v(" Only successful attacks would result in plunder, so something must have been accomplished on the island. Mazeus later went on to campaign on Sardinia (discussed below), where he got most of his army killed. He and the survivors of this force were exiled but returned and supposedly laid siege to Carthage until the leaders capitulated and allowed them to return."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_32",
      "id": "B_ch2_32",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("32")])]), _vm._v(" Although accepted back temporarily, he was eventually tried for crucifying his son and planning a coup to become king, and was executed."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_33",
      "id": "B_ch2_33",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("33")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("We do not possess a precise chronology for the exploits of Mazeus. Orosius claims that he was active during the reign of Cyrus the Great, which places him between 559 and 529 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v("."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_34",
      "id": "B_ch2_34",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("34")])]), _vm._v(" Various dates have been proposed by modern scholars, though none of them are unquestionably convincing."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_35",
      "id": "B_ch2_35",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("35")])]), _vm._v(" It must have been sometime during the sixth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(", and certainly before 509 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(" when Carthage claimed authority in Sicily. I am unconvinced that Mazeus’ campaigns were a direct response to Pentathlus, though it was the period after his attempt to settle in western Sicily that makes sense to me, even if the dating provided by Orosius puts him later in the century. After the threat of new arrivals to western Sicily was realized in Carthage, they could have begun to make plans.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("There is also no indication of against whom Mazeus campaigned. Many scholars propose that it was directed against the Phoenician colonies in an effort to bring them under Carthaginian control."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_36",
      "id": "B_ch2_36",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("36")])]), _vm._v(" David Asheri, for instance, argues that ‘the striking prosperity of sixth-century Selinus and Acragas [sic] speaks eloquently against the assumption that [Mazeus’] ‘‘long wars’’ in Sicily were waged against the Greeks. For this reason it has been very plausibly argued that his enemy may in fact have "), _c('a', {
    attrs: {
      "id": "page_14"
    }
  }), _vm._v("been Punics from Motya and elsewhere.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_37",
      "id": "B_ch2_37",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("37")])]), _vm._v(" E. A. Freeman came to the same conclusion, though perhaps more simplistically, pointing to the eventual leadership of Carthage over the Phoenician cities on Sicily in the sixth century, and concluded that ‘it is hard to avoid the inference that Panormus, Solus, and Motya were brought under the power of Carthage by the arms of [Mazeus].’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_38",
      "id": "B_ch2_38",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("38")])]), _vm._v(" Against this conclusion, though, can be brought two arguments. The first is that a tithe was dedicated to Melqart in Tyre from the loot taken from Sicily. It is improbable that the Carthaginians would have celebrated victory over fellow Phoenicians by making a dedication such as this. Several scholars have pointed to this problem."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_39",
      "id": "B_ch2_39",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("39")])]), _vm._v(" This, to me, is almost insurmountable in arguing that Mazeus conquered the Phoenician settlements on the island. The second argument against this is that the materialization of Carthaginian hegemony in western Sicily does not have to have come about through warfare. The Romans, for instance, relied heavily on diplomacy in the formation of their empire, and there is no reason to reject the idea of Punic leaders peacefully forging a pan-Phoenician alliance on Sicily."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_40",
      "id": "B_ch2_40",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("40")])]), _vm._v(" Because of these two problems, and the absence of specific targets mentioned in our sources, I am inclined to believe that Mazeus’ campaign was primarily one of raiding and plundering, which would help to explain the loyalty of his soldiers, despite his later failure in Sardinia; if they lost no battles, and suffered few casualties on Sicily, they would have been heavily indebted to their general. This does not necessarily preclude this expedition’s role in bringing the Phoenicians of Sicily under the sway of Carthage. It could have also functioned as what a modern strategist may call ‘force projection’, using a display of power to help stabilize a region or accomplish a similar goal, while not necessarily engaging in actual warfare.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Following Mazeus’ actions in Sicily, we hear even fewer details about Carthaginian actions on the island. It is possible that Mago and his sons, whom we have read about in the preceding section, campaigned there in the aftermath of Mazeus’ rebellion and death, but which of these men commanded there is unknown. Justin is vague in the extreme, saying only that, in the wake of Hasdrubal’s death in Sardinia, ‘the people of Sicily, therefore, applying, in consequence of the perpetual depredations of the Carthaginians, to Leonidas, the brother of the king of Sparta, for aid, a grievous war broke out, which continued, with various success, for a long "), _c('a', {
    attrs: {
      "id": "page_15"
    }
  }), _vm._v("period.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_41",
      "id": "B_ch2_41",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("41")])]), _vm._v(" While the inclusion of Leonidas of Sparta is an error, probably for his brother, Dorieus, the rest of this line should not be rejected outright."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_42",
      "id": "B_ch2_42",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("42")])]), _vm._v(" If this is correct, Carthaginian armies were campaigning on Sicily during the time of Mago and his sons. While some authors maintain that this was against the Phoenician cities of the island, as with the campaigns of Mazeus, I find this unlikely, especially in light of Justin’s notice."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_43",
      "id": "B_ch2_43",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("43")])]), _vm._v(" His source, Trogus, implies that the Punic campaigns of this period were against the Greeks, hence the calling for Spartan aid, as it seems improbable that the Phoenician cities would have done this. Even if we reject a connection to Sparta, and thus to the Dorieus incident, as suggested by scholars such as Véronique Krings, this does not mean that we can dismiss the Punic depredations, despite the description of them being ‘vague et conventionnel’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_44",
      "id": "B_ch2_44",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("44")])]), _vm._v(" It is undeniable that some sort of misinterpretation occurred on the part of Trogus and/or Justin, but we should not discount the presence of Punic soldiers on Sicilian soil in the lead up to their confident claims to part of the island in their c. 509 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(" treaty with Rome.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The final thing we hear from Sicily before, or right around, this agreement is the return of Dorieus (discussed in the next section), who, rather than attempting to found another colony in North Africa, sought new fortunes in the area of Mt. Eryx."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_45",
      "id": "B_ch2_45",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("45")])]), _vm._v(" Upon his return to the Peloponnese after having been driven from the River Cinyps, he was convinced to embark on this new venture by a man named Antichares. He told the prince that the territory around Eryx belonged to Herakles who won it as his own, thus it belonged to his descendants."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_46",
      "id": "B_ch2_46",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("46")])]), _vm._v(" As the Spartan royal lines claimed descent from him, this gave Dorieus a good reason to claim the land in western Sicily. He then went to Delphi to consult the oracle, who gave him a positive answer. Returning to Sparta, the prince collected a group of Spartiates and their followers: Thessalus, Paraebates, Celeës, and Euryleon."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_47",
      "id": "B_ch2_47",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("47")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This force set out from the Peloponnese and sailed to the west. Their next move is disputed. According to a Sybarite legend, Dorieus and his companions, before going to Sicily, came to Italy and joined in the war with Croton. The Spartans sided with the Crotonians, and helped in the destruction of Sybaris. The Crotonian version of the war, however, claimed that this was wrong, and the only foreign assistance they had was an exiled Sybarite seer named Callias of Elis."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_48",
      "id": "B_ch2_48",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("48")])]), _vm._v(" Scholarly views on "), _c('a', {
    attrs: {
      "id": "page_16"
    }
  }), _vm._v("which version is correct, and whether or not Dorieus intervened in Italy, are divided, though most believe that the Spartans did participate in the conflict. There are still doubts, however. The presence of Philippos of Croton, who had been exiled from his homeland because he had married a woman from Sybaris, is problematic for both interpretations."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_49",
      "id": "B_ch2_49",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("49")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("For those who support that the expedition went to Italy, we must ask: would the Crotonians be open to help from a group which included one of their exiled citizens? It is possible that his participation would have been an act of contrition for marrying a Sybarite, though this would force us to presume that he had no political enemies amongst the city’s leadership. If his participation was approved of, and he and Dorieus did indeed help Croton, then Philippos not staying in the city, but rather travelling on with the Spartans to Sicily, must be explained."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_50",
      "id": "B_ch2_50",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("50")])]), _vm._v(" It is plausible that they accepted their help and then refused to accept Philippos back, which would be another good reason for the Crotonians to deny any participation at all from the expedition. Luring Dorieus into the war with the false promise of welcoming his friend back into the city would not look good.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("But Philippos’ death on Sicily, and its aftermath, raises questions about this theorized problem. According to Herodotus, the Segestans developed a hero cult which worshipped him sometime after his death, and they offered sacrifices at his tomb and erected there a shrine."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_51",
      "id": "B_ch2_51",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("51")])]), _vm._v(" This was supposedly because of his good looks. This is a bizarre notice. The Segestans, as we shall see below, were involved in the war against Dorieus and his followers. Why, despite his beauty, would they then worship one of their slain enemies? It could have simply been the reason given by Herodotus, as rephrased by Freeman:")]), _vm._v(" "), _c('div', {
    staticClass: "ext"
  }, [_c('p', {
    staticClass: "noindent"
  }, [_vm._v("‘to him were given honours which fell to the lot of no other among his comrades. It shows the deep impression which manly beauty made on the minds of barbarians as well as Greeks that the men of Segesta – it must have been when they came to strip the slain – were overcome by the majestic form, noble even in death, of the victor of Olympia, most beautiful of all the Greeks.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_52",
      "id": "B_ch2_52",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("52")])])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("This is an explanation based on a way of thinking alien to many modern readers, though that is not a good reason to dismiss it. Despite this, I find the entire passage to be unlikely. Segestan seduction by the beauty of a dead man is dubious, as well, the position of this passage in Herodotus’ "), _c('a', {
    attrs: {
      "id": "page_17"
    }
  }), _vm._v("text furthers my doubt. Philippos is not mentioned in the main narrative passages for the Dorieus incident but is rather mentioned as an aside. It is possible that Herodotus, or more likely his informant, confused the Segestan worship of a Crotonian named Philippos with the expedition of Dorieus. It is not inconceivable that such a story could have erroneously coalesced around the hero cult of a Greek amongst the Elymians who inhabited the area around Mt. Eryx.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Whether or not they stopped in Italy, eventually Dorieus and his companions made their way to western Sicily. Here, they attempted to settle. Diodorus is our only source which claims they actually founded a city, but his version is problematic. He claims that their settlement grew quickly and that it made the Carthaginians jealous and they worried that it would soon eclipse their own power."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_53",
      "id": "B_ch2_53",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("53")])]), _vm._v(" This is a massive exaggeration, and there is no way that a newly settled town could eclipse one of its neighbours on Sicily that quickly, let alone Carthage."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_54",
      "id": "B_ch2_54",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("54")])]), _vm._v(" It is more likely that the Lacedaemonians had landed in the neighbourhood of Eryx and were attacked in a temporary camp where they had planned to found their city. According to Herodotus, they were defeated in battle by a combined army of Phoenicians and Segestans, while Pausanias only mentions the latter."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_55",
      "id": "B_ch2_55",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("55")])]), _vm._v(" Were the Carthaginians involved at all?")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The answer to this question is probably yes, though certainly not to the extent claimed by Diodorus, who says that they brought against the new settlement ‘a great army.’ Some authors, such as Dexter Hoyos, see the probable confusion of Phoenicians and Carthaginians in the army which marched against Himera in 480 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(" (see "), _c('a', {
    attrs: {
      "href": "#/xhtml/08_Chapter3"
    }
  }, [_vm._v("next chapter")]), _vm._v(") as evidence that the language used by Herodotus was, to some extent, fluid; thus ‘Phoenicians’ could be used to describe both those of the western Sicilian cities and also the Carthaginians."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_56",
      "id": "B_ch2_56",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("56")])]), _vm._v(" The problem of interpreting Herodotus’ word-choice, however, is more complex than this, as has been exhaustively shown by Véronique Krings."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_57",
      "id": "B_ch2_57",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("57")])]), _vm._v(" It is difficult to say, based solely on linguistic grounds, that the Carthaginians ‘massively intervened in Sicily at the time of the Dorieus expedition’ but this evidence also does not rule it out completely."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_58",
      "id": "B_ch2_58",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("58")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("There is historical support, though, to think that the Carthaginians were probably involved in some way. Near contemporary to Dorieus coming to Sicily, the Carthaginians negotiated a treaty with Rome in which they claimed hegemony over part of Sicily."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_59",
      "id": "B_ch2_59",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("59")])]), _vm._v(" It is inconceivable "), _c('a', {
    attrs: {
      "id": "page_18"
    }
  }), _vm._v("that they would be claiming any part of the island besides that of the western corner, wherein existed the Phoenician cities, and perhaps extending to the Elymian communities. More will be said about this treaty in the concluding section of this chapter, but it is important in our interpretation of Punic participation in the Dorieus affair. If their claim to power in this part of Sicily was true, which it seems like it must have been for them to include it in their treaty with Rome, then they had good reason to not want a Greek interloper founding a new city here. Therefore, I am certain that they participated in some way, though sending an army from Carthage is probably out of the question. In light of the significant role which the Elymians seem to have played, it is quite possible that the Punic role was to finance the campaign, and perhaps to provide a leader or to send supporting troops from the Phoenician cities of Sicily."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_60",
      "id": "B_ch2_60",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("60")])]), _vm._v(" Any conclusion which sees the Carthaginians as having not participated at all must reconcile this with the implication of the Roman treaty."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_61",
      "id": "B_ch2_61",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("61")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Although Punic participation in the short war against Dorieus is a complex subject, there is a perplexing element of the story which must be addressed. That is, the non-participation of the Sicilian Greek cities. No one came to their aid before they were attacked, and only later in the early fifth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(" was a campaign seeking vengeance launched, almost certainly using the story simply as a propaganda tool by Gelon of Syracuse. In fact, after the defeat of Dorieus and his companions, one of the Spartans, Euryleon, the only of them to survive, went on to capture the Selinuntine colony of Minoa, and eventually deposed Peithagoras, the tyrant of Selinus. At this point, the Spartan then took power and ruled, presumably as a tyrant, for a short while before himself being ousted by a popular uprising."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_62",
      "id": "B_ch2_62",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("62")])]), _vm._v(" Selinus could have been allied, in some way, to Carthage for most of the sixth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(", and it was probably trade which drove this."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_63",
      "id": "B_ch2_63",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("63")])]), _vm._v(" This could be part of the explanation as to why Euryleon attacked them specifically. Though the absence of any Greek support of the expedition is telling that the confused memory preserved in Justin, of a Hellenic call for aid to Sparta, is either false or at least does not belong to this era. It is also indicative that the idea of bloc politics, in which Carthaginians, Phoenicians, and Elymians were politically aligned against the Greek city-states "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("en masse")]), _vm._v(" is incorrect and should only be seen, with significant caveats, in a later period.")]), _vm._v(" "), _c('p', {
    staticClass: "head1"
  }, [_c('a', {
    attrs: {
      "id": "page_19"
    }
  }), _c('span', {
    staticClass: "bold"
  }, [_vm._v("Carthaginian Intervention in Sardinia")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Sicily was not the only island important to the new powers of the central Mediterranean. Sardinia, though perhaps more obscure than its larger neighbour, was full of resources sought by Phoenicians, Carthaginians, Etruscans, and Greeks. Precolonial contact between the eastern Mediterranean and the island are evidence of its richness."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_64",
      "id": "B_ch2_64",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("64")])]), _vm._v(" From the eighth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(" onward, Phoenician interest in the island turned into a permanent presence. Although the original draw of the colonists to the island may have been metals, only one of their colonies, Sulcis, was settled in such a spot to principally take advantage of mining territory."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_65",
      "id": "B_ch2_65",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("65")])]), _vm._v(" Its territorial holdings quickly grew, and from the seventh century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(" the people of Sulcis began a programme of building fortifications in the hinterland. This has been interpreted as ‘ensuring direct territorial control of a hinterland rich in lead and silver’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_66",
      "id": "B_ch2_66",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("66")])]), _vm._v(" These fortifications, as well as those of the major Phoenician centres and elsewhere on the island, have been pointed to as more complex than those of the native population and evidence of ‘Phoenician expertise’ in defensive architecture."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_67",
      "id": "B_ch2_67",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("67")])]), _vm._v(" Coupled with the primary settlement’s position on an islet, and its fortifications, it is not to be doubted that its protection was important. This was probably because considerable wealth was created and passed through the city. The wealth of most of the Phoenician cities on Sardinia was likely impressive; notable evidence is the abundance and quality of gold jewellery found there, especially at Tharros."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_68",
      "id": "B_ch2_68",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("68")])]), _vm._v(" This is also suggested by the diversity of imports found in the sites through modern archaeological excavation."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_69",
      "id": "B_ch2_69",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("69")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Establishing themselves on Sardinia may have been seen as important by the early colonists. This is if we are to believe that the high concentration of colonial foundations in the south-west part of the island was part of ‘a genuine territorial strategy’, as some scholars believe."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_70",
      "id": "B_ch2_70",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("70")])]), _vm._v(" This is not a universally accepted idea, and the apparently peaceful process of settlement detected here must be contended with. For instance, Tronchetti has pointed out that we cannot say for sure whether the Phoenicians settled wherever they pleased or if there was a process which involved the local peoples."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_71",
      "id": "B_ch2_71",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("71")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("By the middle of the sixth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(", Carthage became involved with affairs in Sardinia. As with Sicily, our earliest evidence comes from one of our later sources, Justin’s epitome of Pompeius Trogus."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_72",
      "id": "B_ch2_72",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("72")])]), _vm._v(" The same "), _c('a', {
    attrs: {
      "id": "page_20"
    }
  }), _vm._v("Mazeus that we have seen above active on the larger of the two islands led a fateful, unsuccessful, campaign on Sardinia. Having met with enough success on Sicily to send tribute to Tyre, Mazeus and his army sailed to Sardinia where they campaigned against someone. Who this was, though, is unclear. Serge Lancel believed that it was ‘doubtless’ he was ‘grappling with the natives whose pressure on the coastal settlements had to be weakened’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_73",
      "id": "B_ch2_73",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("73")])]), _vm._v(" This conclusion leads to the hypothesis that Mazeus, or perhaps Carthaginian assistance more generally, was called in by the Phoenician settlements. If we accept that he was primarily plundering in Sicily, as argued above, then his army may have campaigned in the same manner once they reached Sardinia.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Just like Mazeus, the early Magonids are also said to have followed a similar, though reversed programme of warfare. They first campaigned in Sardinia, and then later in Sicily. On the former, the sons of Mago, Hasdrubal and Hamilcar, went to war. Hasdrubal was mortally wounded in battle there, and would take his last breath on the island. Before his death he is said to have held the ‘dictatorship’ eleven times, and celebrated four triumphs."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_74",
      "id": "B_ch2_74",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("74")])]), _vm._v(" These are later Latin interpretations of both the office and the celebration, which would have already been translated, at best transliterated, into Greek by the time Pompeius Trogus read about them. Both are to be viewed with scepticism, but probably had some basis in historical truth.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In Rome, a dictator was an office of the Republic in which a man was appointed with supreme authority for a short term in order to deal with a specific crisis. This was in contrast to the typical election of two annual magistrates, the consuls, at the head of the government, who shared power and executive duties. We do not know if Carthage had a similar political position, and the paucity of other evidence as to Hasdrubal’s true position within the Punic government does not help us. Both he and his brother are elsewhere described simply as generals by Justin (Mazeus as well), and perhaps the implication of Hasdrubal’s eleven supposed dictatorships was that he held both a generalship as well as another, higher, political office. The interpretation of the four triumphs may be more straightforward if we assume that it simply means he won four great victories. There may have been a ritual procession, like in Rome or elsewhere in the Mediterranean, as attested in Carthage after the Truceless War in 237."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_75",
      "id": "B_ch2_75",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("75")])]), _vm._v(" Hoyos has proposed that the path of these parades may have gone from the "), _c('a', {
    attrs: {
      "id": "page_21"
    }
  }), _vm._v("Agora to the Byrsa and the temple of Eshmun, a wholly plausible, if only theoretical, reconstruction."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_76",
      "id": "B_ch2_76",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("76")])]), _vm._v(" If this was a common practice in Carthage, we do not hear of it often, but there is no reason to doubt its existence in some form, based on the commonness of celebratory parades and festivals across the Mediterranean world. Some of these may have been celebrated by Hasdrubal after victories in Sardinia, as it does not seem he and his brother were very effective against the Africans (see above).")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Despite the failure of Mazeus in Sardinia, and the death of Hasdrubal while there campaigning, it was probably during his wars, or perhaps more rightly those of his brother Hamilcar, that the Carthaginians won control of the island. They drove all of the hostile peoples of the island into its mountainous interior. Amongst these are numbered some Libyan or Iberian mercenaries, who disagreed with the partition of booty after the, presumably, final campaign. Carthage, apparently, at this juncture settled a number of colonies directly under its control around Sardinia. This, at least, is the picture we get from a single passage in Pausanius."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_77",
      "id": "B_ch2_77",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("77")])]), _vm._v(" Archaeologically, there is evidence for violence on the island in the sixth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(", such as the violent destruction of Cuccureddus, a Phoenician settlement, the partial abandonment of the settlement at Monte Sirai, again Phoenician, and the destruction of Su Nuraxi, an indigenous settlement. Changes in imports, notably the cessation of the import of Ionian pottery and the new importing of Attic pottery, has also been pointed to as an indication of the Carthaginian conquest."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_78",
      "id": "B_ch2_78",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("78")])]), _vm._v(" The problem is, however, none of this is definitive. We have no reason to think the destroyed settlements were because of Punic intervention, just as we cannot say that the Attic imports were due to this rather than shifts in market trends."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_79",
      "id": "B_ch2_79",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("79")])]), _vm._v(" Both positions, that this evidence does and does not indicate a Carthaginian presence, though, are dependent on any particular scholar’s interpretation of Punic ambitions in this period. The vast number of diverging opinions can be seen in the discussion in a recent work by Andrea Roppa."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_80",
      "id": "B_ch2_80",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("80")])]), _vm._v(" Regardless of the position we take here, it cannot be seen as definitive.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("It is likely that Carthage was involved in some way in Sardinia in the second half of the sixth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(". This was almost certainly not a sweeping military conquest; although we have some sites with evidence of destruction during this period, the vast majority of Phoenician and native settlements do not. But this should not be taken as a complete rejection "), _c('a', {
    attrs: {
      "id": "page_22"
    }
  }), _vm._v("of the campaigns of Mazeus and the Magonids. Except for large-scale ‘total war’, we should not expect it to have left a considerable mark in the archaeological record. Raids, pillaging, and punitive actions which only saw pitched battles, or at least battles in the field, will not (generally) be detectable. There is no reason for us to discount the effectiveness that this small-scale warfare could have had in spreading Punic influence on the island,"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_81",
      "id": "B_ch2_81",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("81")])]), _vm._v(" and thus, though they are suspicious, the stories presented by Justin should not be thrown out simply on the grounds that the archaeology does not entirely support them. That Carthage does claim some authority over at least a portion of the island in its first treaty with Rome around 509 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(", still supports their presence.")]), _vm._v(" "), _c('p', {
    staticClass: "head1"
  }, [_c('span', {
    staticClass: "bold"
  }, [_vm._v("Wars with the Phocaeans")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Amongst the stories of early Carthage, conflicts with one group of Greeks are consistent. The sixth century saw them clash with various groups of Phocaean Greeks who had immigrated to the central Mediterranean. Phocaea, their home town, sat on the coast of Anatolia, in modern Turkey. Its inhabitants were Ionian Greeks. The Phocaeans were supposedly the earliest Hellenes to make long voyages, and Herodotus tells us that they were the first to sail the Adriatic and the first to reach Tyrrhenia (probably Central Italy), Iberia and Tartessus."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_82",
      "id": "B_ch2_82",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("82")])]), _vm._v(" They had established such amicable relations with the king of Tartessus, Arganthonius, that he funded a wall for their home town after hearing about the aggression of the Persians."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_83",
      "id": "B_ch2_83",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("83")])]), _vm._v(" In general, Phocaeans seem to have carried on amicably with the native populations of the central and western Mediterranean where they founded their colonies."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_84",
      "id": "B_ch2_84",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("84")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The principal Phocaean settlement in the central Mediterranean was Massalia, modern Marseilles in southern France. It was founded around 600 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(", perhaps in an effort to dominate the tin trade from further north."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_85",
      "id": "B_ch2_85",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("85")])]), _vm._v(" Although it began rather small, it expanded relatively rapidly."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_86",
      "id": "B_ch2_86",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("86")])]), _vm._v(" Although the settlement may have existed somewhat peacefully alongside its native neighbours, this was not the case regarding those with whom their ships shared the sea."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_87",
      "id": "B_ch2_87",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("87")])]), _vm._v(" According to Justin, the city was made rich through fishing and trading, but most of all through piracy."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_88",
      "id": "B_ch2_88",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("88")])]), _vm._v(" If the notice of their ships sailing up the Tiber to Rome during the time of one of the Tarquin kings has any truth to it, this is perhaps an example of one of "), _c('a', {
    attrs: {
      "id": "page_23"
    }
  }), _vm._v("their piratical raids."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_89",
      "id": "B_ch2_89",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("89")])]), _vm._v(" It is behaviour reminiscent of the Viking river raids of much later times. Its absence from the Roman tradition, though, such as that presented by Livy, makes it rather suspect. The behaviour of their sailors, however, did bring them into conflict with the Carthaginians at an unknown date. We could possibly see this as the same clash mentioned around the time of Massalia’s foundation, though this latter notice is usually dated to the influx of Phocaeans to the west after the destruction of their city in 545 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v("."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_90",
      "id": "B_ch2_90",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("90")])]), _vm._v(" At issue in Justin’s narrative was the capture of a number of fishing boats, presumably by the Massaliotes, although the quarrel could have resulted from Punic piracy – our source is not clear."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_91",
      "id": "B_ch2_91",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("91")])]), _vm._v(" The Greeks were victorious and eventually made peace with Carthage, the latter accepting their defeat."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_92",
      "id": "B_ch2_92",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("92")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("It is unclear if this conflict was actually between Carthage and Massalia, though. For much of their early history, the two were separated both by a great distance as well as numerous intervening peoples. Although Justin does describe the Massaliotes’ opponents as Carthaginians ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Karthaginiensium")]), _vm._v("), and Thucydides calls those fought at the foundation of the city in the same way (καρχηδόνιοι), the proximity of Phoenician colonies on Sardinia to Massalia is enough to make us consider that they may have been the Hellenes’ true adversaries. Regardless, our sources are unanimous that the first clash with the Massaliotes ended in a Greek victory.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In 565 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(" a Phocaean colony was established at Alalia, on Corsica."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_93",
      "id": "B_ch2_93",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("93")])]), _vm._v(" Twenty years after its foundation, its population was bolstered by a group who fled the destruction of their hometown by the Persians. These new arrivals began pillaging the settlements of their neighbours and almost certainly preyed on commercial shipping."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_94",
      "id": "B_ch2_94",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("94")])]), _vm._v(" As Herodotus portrays them as primarily antagonizing the Etruscans and Carthaginians, and that the latter responded with naval action, this implies that the Phocaean depredations were being carried out via the sea. After five years of this behaviour, their victims responded. Carthage and the Etruscan city-state of Caere sent a combined fleet of sixty ships coming from each, sailing against the Phocaean fleet of sixty ships total."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_95",
      "id": "B_ch2_95",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("95")])]), _vm._v(" The two groups met somewhere in the Sea of Sardinia, a vague reference made by Herodotus. It has been convincingly argued that this is meant to designate the sea between Sardinia and the Italian peninsula, which would have been the main route connecting the Etruscans and the Phoenicians to the island."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_96",
      "id": "B_ch2_96",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("96")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_24"
    }
  }), _vm._v("When the fleets found one another, a fierce battle ensued, known typically as the Battle of Alalia."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_97",
      "id": "B_ch2_97",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("97")])]), _vm._v(" Despite their numerical advantage, the combined Punic and Etruscan fleet was defeated. We do not hear of their losses, but as the Phocaeans lost forty ships and still ‘won’ the battle, the allied fleet must have suffered considerable casualties; this led Herodotus to describe it as a Cadmean victory. Of the twenty Greek ships which survived the battle, they all had bent rams and were unable to fight, showing the intensity of the struggle."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_98",
      "id": "B_ch2_98",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("98")])]), _vm._v(" These limped back to their port at Alalia, but, in light of their losses, the Phocaeans decided their best course of action would be to abandon their settlement. They loaded women, children, and moveable wealth into what of their ships they had left, and sailed for the Greek colony at Rhegium. From there, they chose a new home and settled a new town called Hyele, known from the Roman period onward as Velia."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_99",
      "id": "B_ch2_99",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("99")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Although the conflicts mentioned above appear as discrete events in our sources, modern scholars have argued that they all are rooted in what became a mangled memory of the Battle of Alalia. Various pieces of literary evidence, and archaeological evidence at Delphi which may indicate the building of a treasury by Massalia around the same time as the battle, have been pointed to as indicating that the references to clashes between the Carthaginians and the Massaliotes all stem from this one event."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_100",
      "id": "B_ch2_100",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("100")])]), _vm._v(" The argument is generally appealing, and the participation of Massalia in the struggle against Carthage and the Etruscans in the seas off Sardinia, standing alongside their Phocaean kinsmen, is sensible. I am reticent, however, to concede that all the notices of conflicts between Carthage and Massalia relate to this one battle. The strongest objection I have is that the absence of Alalia, or even Corsica more generally, from Thucydides and Justin is problematic. Both of them relate their battles to just Massalia."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_101",
      "id": "B_ch2_101",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("101")])]), _vm._v(" It is right to dismiss the importance of Thucydides and Pausanias assigning victories to the Massaliotes over the Carthaginians at the ‘foundation’ of the colony, especially Thucydides appears to use the phrase Φωκαῆς τε Μασσαλίαν οἰκίζοντες to remind his readers, or to educate them on the fact, that Massalia was a Phocaean foundation."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_102",
      "id": "B_ch2_102",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("102")])]), _vm._v(" It could be that the Athenian historian specifically ignored the Battle of Alalia found in Herodotus because it ‘ends with them [the Phocaeans] suffering such losses in battle that they end up abandoning’ the settlement."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_103",
      "id": "B_ch2_103",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("103")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_25"
    }
  }), _vm._v("Any interpretation which suggests all of these notices relate to a single battle is also doing an injustice to the evidence and to Massalia. It turns what was probably a series of clashes between the Phocaean settlers and the Carthaginians into a single, epic, battle. If we read the sources as they are presented to us, we hear of at least one naval victory of Massalia over Carthage (Thucydides, Justin, and Pausanias) and a separate Cadmean victory (or simply a defeat) by the Phocaeans of Alalia, possibly with Massaliote assistance, against both Carthage and an unknown Etruscan power (Herodotus). It is possible, as well, that Massalia is to be credited with two victories over Punic fleets, as Pausanias does mention two different monuments that they deposited at Delphi, though it is unclear if they are meant to commemorate the same or different events."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_104",
      "id": "B_ch2_104",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("104")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Viewed in this light, there were a number of naval engagements fought between the Carthaginians and Phocaean settlers in the north-central Mediterranean around the middle of the sixth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(". This was caused by two simultaneous, though perhaps not directly related, phenomena. The first was developing Punic interest in the island of Sardinia, as shown above by the campaigns of Mazeus and the Magonids, and the colonisation of at least two settlements by Carthage. This would lead them into contact with the second phenomenon, which probably began before a strong Punic presence in the ancient Sea of Sardinia, which was Phocaean piracy. While we do not know if there was a response from the otherwise independent Phoenician cities of Sardinia, once Carthaginian shipping was threatened the problem became personal for the budding imperial city-state. This new player in the region then colluded with their allies in Etruria to bring a strong fleet against the sea-borne raiders, targeting the problematic colony on Corsica, and chased them out of the area. I am partial to seeing the Battle of Alalia ending in a Greek defeat, despite Herodotus calling it a Cadmean victory, which would mean that this was the first naval victory won by Carthage, of which we hear."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_105",
      "id": "B_ch2_105",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("105")])]), _vm._v(" This battle, and the clash(es) between Carthage and Massalia, however, should not be seen as part of a conflict between ethno-political blocs of Phoenicans and Etruscans versus Greeks. As Véronique Krings concluded, we need to see a ‘Mediterranean “in motion”, in which we do not see well-constituted blocs, but rather groups of individuals acting on the basis of personal interests and occasional circumstances’, and that we should not see in these events a ‘framework of grand designs’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/19_Notes#A_ch2_106",
      "id": "B_ch2_106",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("106")])])]), _vm._v(" "), _c('p', {
    staticClass: "head1"
  }, [_c('a', {
    attrs: {
      "id": "page_26"
    }
  }), _c('span', {
    staticClass: "bold"
  }, [_vm._v("Conclusions: Early Punic Ambitions")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Thus, we see that early on in the history of Carthage, territorial ambitions existed. While we must always be wary of how much confidence we put in the stories related by Justin and the other sources, there is a regular theme of early Punic expansion. Interestingly, alongside this we hear about the development of military institutions. The typical thinking that Carthaginians were averse to military service may have in the past made historians doubt these notices, but there are no solid grounds for dismissing them outright. We know that at least in the fourth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(" (as noted in "), _c('span', {
    attrs: {
      "href": "#/xhtml/18_Chapter10",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Chapter 10")]), _vm._v(") Punic citizens took considerable pride in serving in the army. It could very well be that this value of martial skill dates all the way back to the early periods of the city’s history. To the extent that an empire was being forged, though, we must concede that there is little strong evidence for this.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-63655e28","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/xhtml/07_Chapter2.vue
// module id = 78
// module chunks = 0